import { Box, Button, Grid, List, ListItem, Theme, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { completeTasksBtn, setUpServicesBtn } from '../../../Constants/Constants';
import { Features, StageState } from '../../../types/type';
interface ClaimSummaryType {
  headerContent: string;
  subHeaderContent: string | JSX.Element;
  maskSubHeaderInDatadog?: boolean;
}

function getIncompleteStages(availableProgressStages: StageState[]) {
  /* If there are eligible services, the total number of stages will be more than 2 (Report and Complete are in the stages collection by default) */
  const incompleteStages = availableProgressStages.filter((stage) => {
    return stage.id !== Features.REPORT_STAGE && stage.id !== Features.COMPLETE_STAGE && stage.completed === false;
  });
  return incompleteStages;
}

export default function ClaimSummary(props: ClaimSummaryType) {
  const theme: Theme = useTheme();
  const dispatch = useAppDispatch();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const incompleteServices = getIncompleteStages(availableProgressStages);
  let isAutoClaim = false;

  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  if (claimSummary?.autoClaimDetails?.length) {
    isAutoClaim = true;
  }
  const isEmergencyServiceIncomplete =
    incompleteServices.filter((service) => service.id === Features.EMERGENCY_SERVICES).length > 0;
  const isWaterRemovalServiceIncomplete =
    incompleteServices.filter((service) => service.id === Features.WATER_REMOVAL).length > 0;
  const isDocumentDamageServiceIncomplete =
    incompleteServices.filter((service) => service.id === Features.DOCUMENT_DAMAGE).length > 0;

  const isRepairServiceIncomplete = incompleteServices.filter((service) => service.id === Features.REPAIR).length > 0;

  const isRentalServiceIncomplete = incompleteServices.filter((service) => service.id === Features.RENTAL).length > 0;
  const onlyPhotos = incompleteServices.length === 1 && incompleteServices[0].id === Features.PHOTOS;

  const navigate = useNavigate();
  function handleNext() {
    updateLocalStore(incompleteServices[0]);
    switch (incompleteServices[0].id) {
      case Features.EMERGENCY_SERVICES:
        navigate('/emergency-services-home');
        break;
      case Features.WATER_REMOVAL:
        navigate('/emergency-water-removal-home');
        break;
      case Features.DOCUMENT_DAMAGE:
        navigate('/document-damage-services-home');
        break;
      case Features.REPAIR:
        navigate('/repair-home');
        break;
      case Features.PHOTOS:
        navigate('/photos-home');
        break;
      case Features.RENTAL:
        navigate('/rental-home');
        break;
      default:
        navigate('/claim-detail');
    }
  }

  function updateLocalStore(currentStage: StageState) {
    // Update the current stage view active status and make it false for the rest
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
              skipped: false,
            }
          : {
              ...stage,
              viewActive: false,
              showProgress: false,
              skipped: false,
            };
      // When you go back to select the services from Claims Details, you need to reset the '.completed' property of 'COMPLETE_STAGE' stage to 'false'.
      if (stage.id === Features.COMPLETE_STAGE) {
        stage.viewActive = false;
        stage.completed = false;
        stage.showProgress = false;
      }
      // Also, show progress bar as needed
      if (stage.completed || stage.skipped) {
        stage.showProgress = true;
      }
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid item xs={12}>
        <Typography sx={theme.custom.styles.titleType1}>{props.headerContent}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType3,
            marginTop: '1rem',
          }}
          data-dd-privacy={props.maskSubHeaderInDatadog ? 'mask' : undefined}
        >
          {props.subHeaderContent}
        </Typography>
      </Grid>
      {!isAutoClaim && incompleteServices && incompleteServices.length > 0 && (
        <Grid
          item
          xs={12}
          sx={{
            ...theme.custom.styles.pendingTasksBoxStyle,
            marginTop: '1rem',
          }}
        >
          <Typography sx={theme.custom.styles.titleType4}>{'You have services available for your claim!'}</Typography>
          <Typography sx={{ marginTop: '.5rem' }} variant="body1">
            You can <b>Set Up Services </b>online to help protect your property right now.
          </Typography>
          <List sx={theme.custom.styles.confirmationList}>
            {isEmergencyServiceIncomplete && (
              <ListItem sx={theme.custom.styles.confirmationListElement}>
                <Typography variant="body1">Protecting your home by boarding up the damage</Typography>
              </ListItem>
            )}
            {isWaterRemovalServiceIncomplete && (
              <ListItem sx={theme.custom.styles.confirmationListElement}>
                <Typography variant="body1">Emergency water removal assistance</Typography>
              </ListItem>
            )}
            {isDocumentDamageServiceIncomplete && (
              <ListItem sx={theme.custom.styles.confirmationListElement}>
                <Typography variant="body1">Document your damage with Plnar Snap</Typography>
              </ListItem>
            )}
          </List>
          <Button
            onClick={handleNext}
            sx={{
              ...theme.custom.styles.ctaNextButton,
              '&.MuiButtonBase-root:hover': {
                bgcolor: theme.custom.colors.primaryButtonHoverColor,
              },
              marginTop: '1rem',
              marginLeft: '.1rem',
            }}
          >
            {setUpServicesBtn}
          </Button>
        </Grid>
      )}

      {!!isAutoClaim && incompleteServices && incompleteServices.length > 0 && !onlyPhotos && (
        <Grid
          item
          xs={12}
          sx={{
            ...theme.custom.styles.pendingTasksBoxStyle,
            marginTop: '1rem',
          }}
        >
          <Typography sx={theme.custom.styles.titleType4}>{'Would you like to speed up your claim?'}</Typography>
          <Typography sx={{ marginTop: '.5rem' }} variant="body1">
            If you choose to <b>Complete Tasks</b>, we'll guide you through:
          </Typography>
          <List sx={theme.custom.styles.confirmationList}>
            {isRepairServiceIncomplete && (
              <ListItem sx={theme.custom.styles.confirmationListElement}>
                <Typography variant="body1">Setting up your repair</Typography>
              </ListItem>
            )}
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">Uploading photos</Typography>
            </ListItem>
            {isRentalServiceIncomplete && (
              <ListItem sx={theme.custom.styles.confirmationListElement}>
                <Typography variant="body1">Reserving a rental vehicle</Typography>
              </ListItem>
            )}
          </List>
          <Button
            onClick={handleNext}
            sx={{
              ...theme.custom.styles.ctaNextButton,
              '&.MuiButtonBase-root:hover': {
                bgcolor: theme.custom.colors.primaryButtonHoverColor,
              },
              marginTop: '1rem',
              marginLeft: '.1rem',
            }}
          >
            {completeTasksBtn}
          </Button>
        </Grid>
      )}
    </Box>
  );
}
